import React from 'react';
import { ApolloError } from '@apollo/client';
import { BiFile } from 'react-icons/bi';
import { css, keyframes } from '@emotion/css';
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Slide,
  TablePagination,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';

import DefaultFallback from '@components/Fallbacks/DefaultFallback';
import Spacer from '@components/Utils/Spacer';
import ViewModeSwitcher from '@components/Utils/ViewModeSwitcher';

import AppointmentsTable from '@features/appointments/components/AppointmentsTable';
import AppointmentsGrid from '@features/appointments/components/AppointmentsGrid';
import AddAppointmentDialog from '@features/appointments/components/AddAppointmentDialog';
import { useShellTabActions } from '@features/products/shell/shellTabActions';
import { Appointments_Order_By } from '@gql/base-types';

import ExportResultsDialog from '@components/Modals/ExportResultsDialog';
import BuildRunDialog from '../components/BuildRunDialog';
import { AppointmentFilters } from '../export/filterMobileServiceAppointments';
import { useAppointmentsExport } from '../hooks/useAppointmentsExport';
import { capabilityGate } from '@hopdrive/feature-portal-products';
import { useProductEntry } from '@features/products/useProductEntry';
import { GetMobileServiceAppointmentsQuery } from '../queries/getMobileServiceAppointments.query.generated';
import AppointmentsFilterRow from './AppointmentsFilterRow';

type Appointment = GetMobileServiceAppointmentsQuery['appointments'][number];
type ViewMode = 'table' | 'tiles';

// Only un-dispatched, fresh appointments can be put into a run. Statuses are
// lowercase by DB convention (sdk-appointments normalizes them), but legacy
// rows may predate that — compare case-insensitively rather than trusting it.
const isNewAppointment = (a: Appointment) => (a.status ?? '').toLowerCase() === 'new';

interface AppointmentsTabContentProps {
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => Promise<void>;
  /** The visible page. */
  appointments: Appointment[];
  /** How many rows "Export All Results" will write, or `null` when the tab cannot know (a search is active). */
  allMatchingCount: number | null;
  /** The filtered rows the list paginates over. */
  totalCount: number;
  // The export's "all results" query: the tab's customer, sort and filters, with the window below.
  customerId: number;
  orderByGraphQL: Appointments_Order_By[];
  /** The tab's one definition of its client-side filters, shared with the export. */
  filters: AppointmentFilters;
  offset: number;
  limit: number;
  setOffset: (v: number) => void;
  setLimit: (v: number) => void;
  orderBy: string;
  order: 'asc' | 'desc';
  setOrderBy: (v: string) => void;
  setOrder: (v: 'asc' | 'desc') => void;
  // Filters (managed by AppointmentsTab, applied client-side there)
  range: string;
  start: string;
  end: string;
  onRangeChange: (value: string) => void;
  onDateChange: (value: string, name: string) => void;
  search: string;
  setSearch: (value: string) => void;
  selectedStatuses: string[];
  setSelectedStatuses: (statuses: string[]) => void;
}

/**
 * The Appointments tab's content inside the Mobile Service shell. The layout owns the frame, the
 * tab strip, and the header: New Appointment is the product's creation method there, and this
 * tab contributes its refresh as the header's sync and Export CSV in the overflow menu, the
 * Moves page's export in this product's terms.
 */
const AppointmentsTabContent: React.FC<AppointmentsTabContentProps> = ({
  loading,
  error,
  refetch,
  appointments,
  allMatchingCount,
  totalCount,
  customerId,
  orderByGraphQL,
  filters,
  offset,
  limit,
  setOffset,
  setLimit,
  orderBy,
  order,
  setOrderBy,
  setOrder,
  range,
  start,
  end,
  onRangeChange,
  onDateChange,
  search,
  setSearch,
  selectedStatuses,
  setSelectedStatuses,
}) => {
  const theme = useTheme();
  const cls = React.useMemo(() => buildStyles(theme), [theme]);
  // Building a run creates work, so it gates on the Mobile Service product's create capability;
  // editing an appointment is a manage action on the same product.
  const mobileServiceEntry = useProductEntry('mobile-service');
  const canCreate = capabilityGate(mobileServiceEntry, 'createNew').kind === 'render';
  const canManage = capabilityGate(mobileServiceEntry, 'manageExisting').kind === 'render';

  const [editingAppointment, setEditingAppointment] = React.useState<Appointment | null>(null);
  const [buildRunOpen, setBuildRunOpen] = React.useState(false);
  const [checkedItems, setCheckedItems] = React.useState<Appointment[]>([]);
  const [viewMode, setViewMode] = React.useState<ViewMode>(() => {
    return (localStorage.getItem('ms-appointments-view-mode') as ViewMode) || 'table';
  });

  const selectViewMode = (newMode: ViewMode) => {
    localStorage.setItem('ms-appointments-view-mode', newMode);
    setViewMode(newMode);
  };

  // The CSV export: the dialog's state, "current page" from the rows on screen, "all results"
  // from its own query over the window with the same filters.
  const { exportDialogOpen, openExportDialog, closeExportDialog, exportCurrentPage, exportAll } = useAppointmentsExport({
    customerId,
    windowStartIso: start,
    windowEndIso: end,
    orderBy: orderByGraphQL,
    filters,
  });

  // The tab's contribution to the header: its refresh, and Export CSV in the overflow.
  const nothingToExport = totalCount === 0;
  const tabActions = React.useMemo(
    () => ({
      sync: refetch,
      overflow: [
        {
          key: 'export-csv',
          label: 'Export CSV',
          icon: <BiFile />,
          onSelect: openExportDialog,
          disabledReason: nothingToExport ? 'No appointments to export' : undefined,
        },
      ],
    }),
    [refetch, openExportDialog, nothingToExport]
  );
  useShellTabActions(tabActions);

  const [loadingVisible, setLoadingVisible] = React.useState(false);
  React.useEffect(() => {
    if (loading) {
      setLoadingVisible(true);
      return;
    }
    // Cleanup matters: without it, a loading false→true flip within the
    // 250ms window leaves a stale timer that hides the bar mid-load.
    const timer = setTimeout(() => setLoadingVisible(false), 250);
    return () => clearTimeout(timer);
  }, [loading]);

  return (
    <>
      {/* Edit variant of the appointment dialog, keyed off the row the user picked. The create
          variant is the product's creation method, mounted by the layout. */}
      <AddAppointmentDialog
        open={Boolean(editingAppointment)}
        appointment={editingAppointment}
        onClose={() => setEditingAppointment(null)}
        onSuccess={() => { setEditingAppointment(null); refetch(); }}
      />

      <ExportResultsDialog
        open={exportDialogOpen}
        onClose={closeExportDialog}
        title='Export Appointments'
        noun='appointment'
        totalCount={allMatchingCount}
        currentPageCount={appointments.length}
        onExportAll={exportAll}
        onExportCurrentPage={() => exportCurrentPage(appointments)}
      />

      <BuildRunDialog
        open={buildRunOpen}
        onClose={() => setBuildRunOpen(false)}
        onSuccess={() => { setBuildRunOpen(false); setCheckedItems([]); refetch(); }}
        // Defensive: only 'new' appointments can be put into a run. The table's
        // isRowSelectable already prevents non-'new' rows from being checked,
        // but if a row's status changed underneath the user (e.g. another tab
        // dispatched it) we filter here as the final gate.
        initialAppointments={checkedItems.filter(isNewAppointment)}
      />

      <div className={cls.header}>
        <Box sx={{ px: 2.5, pt: 3 }}>
          <AppointmentsFilterRow
            range={range}
            start={start}
            end={end}
            onRangeChange={onRangeChange}
            onDateChange={onDateChange}
            disablePickers={range !== 'custom'}
            search={search}
            setSearch={setSearch}
            selectedStatuses={selectedStatuses}
            setSelectedStatuses={setSelectedStatuses}
            leading={<ViewModeSwitcher size='small' value={viewMode} onChange={selectViewMode} options={['table', 'tiles']} />}
          />
        </Box>
      </div>

      {loadingVisible ? (
        <LinearProgress className={loading ? cls.fadeIn : cls.fadeOut} />
      ) : (
        <div className={cls.loadingPad} />
      )}

      {error ? (
        <>
          <Spacer size={2} />
          <DefaultFallback fullscreen severity='error' message='Failed to fetch appointments.' />
        </>
      ) : null}

      {!loading && !error && appointments.length === 0 ? (
        <>
          <Spacer size={2} />
          <DefaultFallback
            fullscreen
            severity='warning'
            message='No mobile service appointments found. Try adjusting your date range or create a new appointment.'
          />
        </>
      ) : null}

      {!error && appointments.length > 0 ? (
        <>
          <TablePagination
            className={cls.pagination}
            component='div'
            count={totalCount}
            page={Math.floor(offset / limit)}
            onPageChange={(_e, p) => setOffset(p * limit)}
            rowsPerPage={limit}
            onRowsPerPageChange={e => { setLimit(parseInt(e.target.value, 10)); setOffset(0); }}
            rowsPerPageOptions={[25, 50, 100]}
          />

          <div className={loading ? cls.contentLoading : cls.content}>
            {viewMode === 'table' ? (
              <AppointmentsTable
                appointments={appointments}
                totalCount={totalCount}
                orderBy={orderBy}
                order={order}
                setOrderBy={setOrderBy}
                setOrder={setOrder}
                setOffset={setOffset}
                showRunColumn
                checkedItems={checkedItems}
                setCheckedItems={setCheckedItems}
                // Only un-dispatched, fresh appointments can be added to a new
                // run. Anything past 'new' (pending / ready / completed / missed /
                // canceled) stays visible but its checkbox renders disabled.
                isRowSelectable={isNewAppointment}
                onEdit={setEditingAppointment}
                editAllowed={canManage}
                // Every row here is type mobile_service; reclaim the width so
                // the actions column fits without horizontal scroll.
                hideTypeColumn
              />
            ) : (
              <AppointmentsGrid appointments={appointments} />
            )}

            <TablePagination
              className={cls.pagination}
              component='div'
              count={totalCount}
              page={Math.floor(offset / limit)}
              onPageChange={(_e, p) => setOffset(p * limit)}
              rowsPerPage={limit}
              onRowsPerPageChange={e => { setLimit(parseInt(e.target.value, 10)); setOffset(0); }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </div>
        </>
      ) : null}

      {/* Selection action bar — anchored to the bottom of the page-content
          scroll area when one or more appointments are checked. Sticky (not
          fixed) so it naturally respects the left-nav's horizontal bounds and
          won't slide behind it regardless of the nav's condensed state.
          Hopdrive styling: dark grounding control panel against the light
          canvas for maximum visual contrast (per brand rule — "dark grounding
          filter/control panels against light content"). elevation.level4 lift
          to feel like a floating command bar. */}
      <Slide direction='up' in={checkedItems.length > 0} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: 'sticky',
            bottom: theme.spacing(2),
            mx: 2,
            mb: 2,
            zIndex: theme.zIndex.appBar,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: 3,
            py: 2,
            borderRadius: 2,
            bgcolor: 'utility.darker',
            color: '#fff',
            boxShadow: '0px 16px 48px rgba(0,0,0,0.18), 0px 8px 16px rgba(0,0,0,0.08)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={checkedItems.length}
              sx={{
                bgcolor: 'primary.main',
                color: '#fff',
                fontWeight: 700,
                fontSize: 14,
                minWidth: 36,
                height: 32,
              }}
            />
            <Typography variant='body1' sx={{ fontWeight: 500, color: '#fff' }}>
              {checkedItems.length === 1 ? 'appointment' : 'appointments'} selected, ready to build a service run
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button
              variant='text'
              onClick={() => setCheckedItems([])}
              sx={{
                color: 'rgba(255,255,255,0.7)',
                '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
              }}
            >
              Clear
            </Button>
            {canCreate ? (
              <Button
                variant='contained'
                color='primary'
                onClick={() => setBuildRunOpen(true)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Build Run
              </Button>
            ) : null}
          </Box>
        </Box>
      </Slide>
    </>
  );
};

// STYLES ---------------------------------------------------------------- //

const fadeIn = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`;

const fadeOut = keyframes`
  0% { opacity: 1; }
  100% { opacity: 0; }
`;

const contentFadeIn = keyframes`
  0% { opacity: 0; }
  33% { opacity: 0; }
  100% { opacity: 1; }
`;

// Pure function (no hooks) — safe to call inside useMemo. Named buildStyles
// rather than useStyles so it isn't mistaken for a hook.
const buildStyles = (theme: Theme) => {
  return {
    header: css`
      background-color: ${theme.palette.background.paper};
      border-bottom: 1px solid ${theme.palette.divider};
    `,
    fadeIn: css`
      opacity: 1;
      animation: ${fadeIn} 0.25s ease-in-out;
    `,
    fadeOut: css`
      opacity: 0;
      animation: ${fadeOut} 0.25s ease-in-out;
    `,
    loadingPad: css`
      width: 100%;
      height: 4px;
    `,
    content: css`
      opacity: 1;
      transition: opacity 0.25s ease-in-out;
      animation: ${contentFadeIn} 0.5s ease-in-out;
    `,
    contentLoading: css`
      opacity: 0;
      transition: opacity 0.25s ease-in-out;
      user-select: none;
      pointer-events: none;
    `,
    pagination: css`
      border-bottom: 1px solid ${theme.palette.divider};
      margin-right: 16px;
    `,
  };
};

export default AppointmentsTabContent;
